import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import './AuthPage.css';
import './VerificationPage.css';

export default function VerificationPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Email passed from RegisterPage via navigation state
    const email = location.state?.email || '';
    const pendingToken = location.state?.token || '';
    const pendingUser = location.state?.user || null;

    const [digits, setDigits] = useState(['', '', '', '', '']);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [countdown, setCountdown] = useState(60); // resend cooldown
    const [canResend, setCanResend] = useState(false);
    const inputsRef = useRef([]);

    // Redirect if no email in state
    useEffect(() => {
        if (!email) navigate('/register');
    }, [email, navigate]);

    // Countdown timer for resend button
    useEffect(() => {
        if (countdown <= 0) { setCanResend(true); return; }
        const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [countdown]);

    // Auto-send code on mount
    useEffect(() => {
        if (email) sendCode();
        // eslint-disable-next-line
    }, []);

    const sendCode = async () => {
        try {
            await api.post('/auth/send-verification', { email });
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to send code.');
        }
    };

    // Handle each digit input
    const handleDigitChange = (index, value) => {
        // Only allow numbers
        const cleaned = value.replace(/\D/g, '').slice(-1);
        const newDigits = [...digits];
        newDigits[index] = cleaned;
        setDigits(newDigits);
        setError('');

        // Auto-advance to next input
        if (cleaned && index < 4) {
            inputsRef.current[index + 1]?.focus();
        }

        // Auto-submit when all 5 digits filled
        if (cleaned && index === 4) {
            const code = [...newDigits.slice(0, 4), cleaned].join('');
            if (code.length === 5) handleVerify(code);
        }
    };

    const handleKeyDown = (index, e) => {
        // Backspace: clear current and go back
        if (e.key === 'Backspace' && !digits[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
        // Arrow keys
        if (e.key === 'ArrowLeft' && index > 0) inputsRef.current[index - 1]?.focus();
        if (e.key === 'ArrowRight' && index < 4) inputsRef.current[index + 1]?.focus();
    };

    // Handle paste (e.g. user pastes "12345")
    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
        if (pasted.length === 5) {
            setDigits(pasted.split(''));
            inputsRef.current[4]?.focus();
            handleVerify(pasted);
        }
    };

    const handleVerify = async (codeOverride) => {
        const code = codeOverride || digits.join('');
        if (code.length < 5) {
            setError('Please enter the complete 5-digit code.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await api.post('/auth/verify-code', { email, code });
            setSuccess('Email verified successfully! Redirecting to your dashboard…');

            // Log the user in automatically
            setTimeout(() => {
                if (pendingToken && pendingUser) {
                    login(pendingToken, pendingUser);
                    navigate('/dashboard');
                } else {
                    navigate('/login');
                }
            }, 1500);
        } catch (err) {
            setError(err.response?.data?.error || 'Invalid code. Please try again.');
            // Shake the inputs
            setDigits(['', '', '', '', '']);
            inputsRef.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!canResend || resending) return;
        setResending(true);
        setError('');
        setDigits(['', '', '', '', '']);
        inputsRef.current[0]?.focus();
        try {
            await api.post('/auth/resend-code', { email });
            setCanResend(false);
            setCountdown(60);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to resend code.');
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="page-wrapper">
            <Header />
            <main className="main-content auth-main">
                <div className="verify-container">
                    <div className="auth-card verify-card">

                        {/* Icon */}
                        <div className="verify-icon-wrapper">
                            <div className="verify-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                    <polyline points="22,6 12,13 2,6" />
                                </svg>
                            </div>
                        </div>

                        <div className="auth-header">
                            <h1>Check Your Email</h1>
                            <p>
                                We sent a 5-digit verification code to<br />
                                <strong className="verify-email">{email}</strong>
                            </p>
                        </div>

                        {/* Success */}
                        {success && (
                            <div className="verify-success">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18">
                                    <path d="M20 6L9 17l-5-5" />
                                </svg>
                                {success}
                            </div>
                        )}

                        {/* Error */}
                        {error && <div className="auth-error">{error}</div>}

                        {/* 5-digit input boxes */}
                        <div className="verify-digits" onPaste={handlePaste}>
                            {digits.map((digit, i) => (
                                <input
                                    key={i}
                                    ref={el => inputsRef.current[i] = el}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    className={`verify-digit-input ${digit ? 'filled' : ''} ${error ? 'error' : ''}`}
                                    value={digit}
                                    onChange={e => handleDigitChange(i, e.target.value)}
                                    onKeyDown={e => handleKeyDown(i, e)}
                                    onFocus={e => e.target.select()}
                                    disabled={loading || !!success}
                                    autoFocus={i === 0}
                                    aria-label={`Digit ${i + 1}`}
                                />
                            ))}
                        </div>

                        {/* Verify button */}
                        <button
                            className="btn btn-primary auth-submit-btn"
                            onClick={() => handleVerify()}
                            disabled={loading || digits.join('').length < 5 || !!success}
                        >
                            {loading ? (
                                <span className="verify-loading">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18"
                                        style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>
                                        <circle cx="12" cy="12" r="10" strokeDasharray="60" strokeDashoffset="20" />
                                    </svg>
                                    Verifying…
                                </span>
                            ) : 'Verify Email'}
                        </button>

                        {/* Resend */}
                        <div className="verify-resend">
                            <span>Didn't receive the code?</span>
                            {canResend ? (
                                <button
                                    className="verify-resend-btn"
                                    onClick={handleResend}
                                    disabled={resending}
                                >
                                    {resending ? 'Sending…' : 'Resend Code'}
                                </button>
                            ) : (
                                <span className="verify-countdown">
                                    Resend in <strong>{countdown}s</strong>
                                </span>
                            )}
                        </div>

                        <div className="verify-disclaimer">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            Code expires in 10 minutes. Check your spam folder if you don't see it.
                        </div>

                        <div className="auth-footer">
                            <Link to="/register">← Back to Registration</Link>
                        </div>

                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}